/**
 * Cell formatting seam for the kit (CD-7: kit renders ONLY from typed props).
 * Every formatter here takes a Cell — never a naked numeric — so a bare number
 * cannot sneak into the grid without its trust envelope.
 */

import { formatCurrency, formatNumber, formatPercentage } from '../../utils/formatters'
import type { Cell, MetricFormat, Rag } from '../../types/metrics'

export const EM_DASH = '—'

/**
 * The single source of the §4 display scale: master Value is raw, so gbp_k renders
 * ÷1000 (£k) and pct is a 0.xx decimal ×100. count/days are unscaled. formatCellValue
 * (string) and the trend chart's numeric line/axis both go through this so a value can
 * never be plotted one way and tabulated another (CD-12).
 */
export function scaleForDisplay(value: number, format: MetricFormat): number {
  if (format === 'gbp_k') return value / 1000
  if (format === 'pct') return value * 100
  return value
}

/** Runtime guard behind the TS types: does this look like a served Cell? */
export function isCell(candidate: unknown): candidate is Cell {
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    'metric_id' in candidate &&
    'plan_type' in candidate &&
    'format' in candidate &&
    'value' in candidate
  )
}

/**
 * Format a Cell's served value per its registry format (§4):
 * gbp_k — master Value is raw £, rendered ÷1000 as £k (1 dp under £100k);
 * pct — the master serves ratio KPIs as DECIMALS (0.xx: the captured
 * dev_vw_KPI_All_Monthly_Agg header pins "Pct - Pre-calculated percentage as
 * decimal", and the master maps Value = Pct for OTIF/Utilisation/NC-Batch/
 * Absence Rate; GPM is a derived 0–1 ratio), so ×100 before the carried
 * formatter, whose input contract is "already in percentage, e.g. 75.5";
 * count/days — the carried formatters. NULL value renders the em-dash.
 */
export function formatCellValue(cell: Cell): string {
  return formatMetricValue(cell.value, cell.format)
}

/** The same rules against the cell's YEAR-TO-DATE leg. Split out rather than duplicated so
 *  a £k or a percentage cannot come to read differently in the YTD column than in June. */
export function formatCellYtd(cell: Cell): string {
  return formatMetricValue(cell.ytd_value, cell.format)
}

function formatMetricValue(value: number | null, format: MetricFormat): string {
  if (value === null) return EM_DASH
  switch (format) {
    case 'gbp_k': {
      const thousands = scaleForDisplay(value, 'gbp_k')
      return `${formatCurrency(thousands, '£', Math.abs(thousands) < 100 ? 1 : 0)}k`
    }
    case 'pct':
      return formatPercentage(scaleForDisplay(value, 'pct'))
    case 'count':
    case 'days':
      return formatNumber(value, 0)
  }
}

/** The DQ badge for a set of dq_flags: KNOWN-WRONG (bad) beats a generic DQ (warn);
 * null when clean. One source for the cell, the trend panel and the analytics card. */
export function dqBadge(flags: string[]): { label: string; tone: 'bad' | 'warn' } | null {
  if (flags.length === 0) return null
  const knownWrong = flags.some((flag) => flag.toUpperCase().includes('KNOWN-WRONG'))
  return knownWrong ? { label: 'KNOWN-WRONG', tone: 'bad' } : { label: 'DQ', tone: 'warn' }
}

/** §5 RAG rule classes: green meets-or-beats, red otherwise, none = neutral grey. */
export const RAG_CLASSES: Record<Rag, string> = {
  green: 'text-emerald-600 dark:text-emerald-400',
  red: 'text-red-600 dark:text-red-400',
  none: 'text-slate-500 dark:text-slate-400',
}

/** Column heading for a §5 period label: '2026-01' → 'Jan 26', '2026-W02' → 'W02'. */
export function periodHeading(period: string): string {
  const weekly = period.match(/^\d{4}-(W\d{2})$/)
  if (weekly) return weekly[1]
  const [year, month] = period.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString('en-GB', {
    month: 'short',
    year: '2-digit',
  })
}

/**
 * Has this period ENDED? Monthly periods only ('2026-07'); a weekly id has no month to
 * close, so it always reads as complete and the anchor behaves as it always did.
 *
 * Calendar arithmetic, deliberately — not a size threshold. "Is this month over" has one
 * right answer; "is this month big enough to headline" does not, and a threshold would be
 * a number nobody could defend the day it excluded a genuinely quiet month.
 */
export function isPeriodComplete(period: string, now: Date = new Date()): boolean {
  if (!/^\d{4}-\d{2}$/.test(period)) return true
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  return period < current
}
